import { useState } from "react"
import { FaEdit, FaFile, FaFilePdf, FaFileWord, FaVideo, FaImage, FaCheckCircle } from "react-icons/fa"
import { Button } from "@/components/ui/button"
import { ResourceType } from "../Interfaces"
import { DescriptionDialog } from "./DescriptionDialog"

interface UploadedFile {
    name: string
    type: string
    size: number
}

interface UploadedFilesListProps {
    uploadedFiles: UploadedFile[]
    resourceType: ResourceType
    onDescriptionUpdated: () => void
}

export function UploadedFilesList({ 
    uploadedFiles, 
    resourceType, 
    onDescriptionUpdated 
}: UploadedFilesListProps) {
    const [descriptionDialog, setDescriptionDialog] = useState<{
        isOpen: boolean
        fileName: string
    }>({ isOpen: false, fileName: '' })

    const openDescriptionDialog = (fileName: string) => {
        setDescriptionDialog({ isOpen: true, fileName })
    }

    const closeDescriptionDialog = () => {
        setDescriptionDialog({ isOpen: false, fileName: '' })
    }

    if (uploadedFiles.length === 0) {
        return null
    }

    return (
        <>
            <div className="mt-6 bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-4">
                    <FaCheckCircle className="w-5 h-5 text-green-600" />
                    <h3 className="text-lg font-semibold text-green-800">
                        Successfully Uploaded ({uploadedFiles.length} files)
                    </h3>
                </div>
                
                <div className="space-y-3">
                    {uploadedFiles.map((file) => (
                        <UploadedFileCard
                            key={file.name}
                            file={file}
                            onEditDescription={() => openDescriptionDialog(file.name)}
                        />
                    ))}
                </div>
                
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-700">
                        💡 <strong>Tip:</strong> Add descriptions to your files to make them easier to find and understand later!
                    </p>
                </div>
            </div>

            <DescriptionDialog
                isOpen={descriptionDialog.isOpen}
                onClose={closeDescriptionDialog}
                fileName={descriptionDialog.fileName}
                resourceType={resourceType}
                onDescriptionUpdated={onDescriptionUpdated}
            />
        </>
    )
}

function UploadedFileCard({ 
    file, 
    onEditDescription 
}: { 
    file: UploadedFile
    onEditDescription: () => void 
}) {
    return (
        <div className="flex items-center justify-between bg-white border border-green-200 rounded-lg p-3">
            <div className="flex items-center gap-3">
                <FileTypeIcon type={file.type} />
                <div>
                    <div className="font-medium text-gray-900">{file.name}</div>
                    <div className="text-sm text-gray-500">
                        {formatFileSize(file.size)} • {file.type.toUpperCase()}
                    </div>
                </div>
            </div>
            
            <Button
                variant="outline"
                size="sm"
                onClick={onEditDescription}
                className="flex items-center gap-2 text-blue-600 border-blue-200 hover:bg-blue-50"
            >
                <FaEdit className="w-3 h-3" />
                Add Description
            </Button>
        </div>
    )
}

function FileTypeIcon({ type }: { type: string }) {
    const getFileIcon = (fileType: string) => {
        switch (fileType.toLowerCase()) {
            case 'pdf':
                return <FaFilePdf className="w-6 h-6 text-red-500" />
            case 'docx':
                return <FaFileWord className="w-6 h-6 text-blue-500" />
            case 'mp4':
                return <FaVideo className="w-6 h-6 text-purple-500" />
            case 'jpg':
            case 'png':
                return <FaImage className="w-6 h-6 text-green-500" />
            default:
                return <FaFile className="w-6 h-6 text-gray-500" />
        }
    }

    return (
        <div className="flex items-center justify-center w-10 h-10 bg-gray-50 rounded-lg border">
            {getFileIcon(type)}
        </div>
    )
}

function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}
